import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The small shared pieces. Status is the one worth reading carefully.
 */

type Status = 'up' | 'warn' | 'down' | 'unknown';

const STATUS_META: Record<Status, { icon: string; label: string }> = {
  // Each status carries an icon AND a word. Colour is the third channel, never
  // the only one — red/green alone fails for roughly 8% of men, and "is that host
  // up?" is exactly the question you cannot afford to answer ambiguously.
  up: { icon: '●', label: 'Up' },
  warn: { icon: '▲', label: 'Degraded' },
  down: { icon: '■', label: 'Down' },
  unknown: { icon: '○', label: 'Unknown' },
};

export function statusOf(raw: string | null | undefined): Status {
  switch (raw) {
    case 'UP':
    case 'RUNNING':
    case 'HEALTHY':
      return 'up';
    case 'DEGRADED':
    case 'RESTARTING':
    case 'STARTING':
    case 'PAUSED':
      return 'warn';
    case 'UNREACHABLE':
    case 'EXITED':
    case 'DEAD':
    case 'UNHEALTHY':
      return 'down';
    default:
      return 'unknown';
  }
}

export function StatusPill({ status, label }: { status: string | null | undefined; label?: string }) {
  const s = statusOf(status);
  const meta = STATUS_META[s];
  return (
    <span className={`pill pill-${s}`}>
      <span aria-hidden="true">{meta.icon}</span>
      {label ?? meta.label}
    </span>
  );
}

/**
 * A single number and its label.
 *
 * Not every figure deserves a chart. "20 containers running" is a fact, and a
 * fact reads faster as a number than as a bar of length one.
 */
export function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Status;
}) {
  return (
    <div className={`tile${tone ? ` tile-${tone}` : ''}`}>
      <div className="tile-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </div>
  );
}

/**
 * A horizontal usage bar for a single ratio.
 *
 * Deliberately not a chart: one value against a known maximum is a meter, and it
 * always carries its number beside it so the bar is reinforcement rather than the
 * only reading.
 */
export function Meter({ value, label }: { value: number | null; label: string }) {
  if (value === null) return <span className="muted">—</span>;
  const pct = Math.max(0, Math.min(100, value));
  // Thresholds are the same ones the alert rules will use. A meter that says
  // "fine" while an alert fires would be two sources of truth.
  const tone: Status = pct >= 90 ? 'down' : pct >= 75 ? 'warn' : 'up';
  return (
    <div className="meter" title={`${label}: ${pct.toFixed(1)}%`}>
      <div className="meter-track">
        <div className={`meter-fill meter-${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="meter-value num">{pct.toFixed(0)}%</span>
    </div>
  );
}

export function Card({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

/**
 * A sortable column header.
 *
 * The header is a real <button> inside the <th>, not a click handler on the cell:
 * that makes it reachable by keyboard and announced as actionable. `aria-sort`
 * tells a screen reader which column is ordering the table and which way — the
 * caret alone conveys that to sighted users only.
 */
export function SortHeader({
  label,
  sortKey,
  state,
  numeric,
}: {
  label: string;
  sortKey: string;
  state: { key: string; dir: 'asc' | 'desc'; toggle: (k: string) => void };
  numeric?: boolean;
}) {
  const active = state.key === sortKey;
  return (
    <th
      className={numeric ? 'num' : undefined}
      aria-sort={active ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="th-sort" onClick={() => state.toggle(sortKey)}>
        {label}
        <span className="sort-caret" aria-hidden="true">
          {active ? (state.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  );
}

export type EntityType = 'client' | 'server' | 'product' | 'container' | 'llm' | 'endpoint';

/**
 * Each entity gets a glyph as well as a hue.
 *
 * Colour alone would leave the whole scheme invisible to a colourblind reader and
 * to anyone printing a page — and this palette deliberately includes a red and a
 * green, which is the exact pair that fails. The glyph is the redundant channel.
 */
const ENTITY_GLYPH: Record<EntityType, string> = {
  client: '◆',
  server: '▣',
  product: '●',
  container: '▪',
  llm: '✦',
  endpoint: '⇢',
};

/**
 * A labelled chip identifying one entity.
 *
 * The hue lives in the glyph and the background tint; the label always wears
 * `--ink`. That is what makes the light-mode contrast warning on green and yellow
 * a non-issue — coloured text at 2.05:1 would be unreadable, a coloured dot beside
 * black text is not.
 */
export function EntityChip({
  type,
  label,
  meta,
  tag,
  title,
}: {
  type: EntityType;
  label: ReactNode;
  /** A count or version, set apart from the name. */
  meta?: ReactNode;
  /** A short qualifier, e.g. "owner". */
  tag?: ReactNode;
  title?: string;
}) {
  return (
    <span className={`echip echip-${type}`} title={title}>
      <span className="echip-glyph" aria-hidden="true">{ENTITY_GLYPH[type]}</span>
      <span className="echip-label">{label}</span>
      {meta !== undefined && meta !== null && <span className="echip-meta">{meta}</span>}
      {tag && <span className="echip-tag">{tag}</span>}
    </span>
  );
}

/**
 * A modal dialog, built on the native <dialog> element.
 *
 * Native rather than a hand-rolled overlay because the browser then supplies the
 * three things these get wrong: focus is trapped inside while open, Escape
 * closes, and the backdrop is a real top-layer paint rather than a z-index
 * guess. The cost is that opening must go through showModal() in an effect,
 * since React cannot express "open" as a prop the element honours.
 */
export function Modal({
  open,
  onClose,
  title,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** For content that is a document rather than a form. */
  wide?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      className={wide ? 'modal modal-wide' : 'modal'}
      // Fires on Escape as well as close(), so this is the one place that has to
      // tell the parent — otherwise Escape would close the dialog visually while
      // the parent still believed it open, and it could never be reopened.
      onCancel={onClose}
      onClose={onClose}
      // Clicking the backdrop closes. The target is the dialog itself only when
      // the click missed every child, which is exactly what a backdrop click is.
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-head">
        <h2>{title}</h2>
        <button type="button" className="ghost" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}
